/**
 * Lead Command Center — Edit Lead / New Manual Lead dialogs.
 *
 * The ONLY place the less-common lead fields (source, campaign, org/contact
 * linking, assignee) are editable. The normal daily workflow goes through
 * Record Interaction instead; Priority is intentionally not exposed anywhere.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { lccApi, type LccLead, type LccLeadPatch } from '@/lib/lcc-api';
import { formatLccLabel } from './lcc-shared';
import { LCC_ASSIGNEES, useLccInvalidate } from './lead-actions';

export const LCC_SOURCES = [
  'website_form',
  'phone_call',
  'google_ads',
  'facebook_ads',
  'referral',
  'email',
  'chat',
  'manual_entry',
  'imported',
];

interface LeadFormState {
  title: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  campaign: string;
  message: string;
  source: string;
  assignedTo: string;
  organizationId: string;
  contactId: string;
}

const EMPTY_FORM: LeadFormState = {
  title: '',
  contactName: '',
  email: '',
  phone: '',
  website: '',
  campaign: '',
  message: '',
  source: 'manual_entry',
  assignedTo: '__unassigned',
  organizationId: '__none',
  contactId: '__none',
};

function formToPayload(form: LeadFormState): LccLeadPatch & { title: string } {
  return {
    title: form.title,
    contactName: form.contactName || null,
    email: form.email || null,
    phone: form.phone || null,
    website: form.website || null,
    campaign: form.campaign || null,
    message: form.message || null,
    source: form.source,
    assignedTo: form.assignedTo === '__unassigned' ? null : form.assignedTo,
    organizationId: form.organizationId === '__none' ? null : form.organizationId,
    contactId: form.contactId === '__none' ? null : form.contactId,
  };
}

function LeadFormDialogBase({
  open,
  onOpenChange,
  dialogTitle,
  submitLabel,
  initial,
  pending,
  error,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dialogTitle: string;
  submitLabel: string;
  initial: LeadFormState;
  pending: boolean;
  error: unknown;
  onSubmit: (payload: LccLeadPatch & { title: string }) => void;
}) {
  const [form, setForm] = useState(initial);

  const orgsQuery = useQuery({
    queryKey: ['lcc', 'organizations'],
    queryFn: () => lccApi.organizations(),
    enabled: open,
  });
  const contactsQuery = useQuery({
    queryKey: ['lcc', 'contacts'],
    queryFn: () => lccApi.contacts(),
    enabled: open,
  });

  // Reset the form each time the dialog opens for (possibly refetched) data.
  const [lastOpen, setLastOpen] = useState(false);
  if (open && !lastOpen) {
    setLastOpen(true);
    setForm(initial);
  } else if (!open && lastOpen) {
    setLastOpen(false);
  }

  const set =
    (key: keyof LeadFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));
  const setValue = (key: keyof LeadFormState) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const inputCls =
    'flex h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] font-normal text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
  const labelCls = 'flex flex-col gap-1 text-[12px] font-medium text-muted-foreground';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2.5 sm:grid-cols-2">
          <label className={`${labelCls} sm:col-span-2`}>
            Title
            <input value={form.title} onChange={set('title')} className={inputCls} />
          </label>
          <label className={labelCls}>
            Contact name
            <input value={form.contactName} onChange={set('contactName')} className={inputCls} />
          </label>
          <label className={labelCls}>
            Email
            <input value={form.email} onChange={set('email')} className={inputCls} />
          </label>
          <label className={labelCls}>
            Phone
            <input value={form.phone} onChange={set('phone')} className={inputCls} />
          </label>
          <label className={labelCls}>
            Website
            <input value={form.website} onChange={set('website')} className={inputCls} />
          </label>
          <label className={labelCls}>
            Source
            <Select value={form.source} onValueChange={setValue('source')}>
              <SelectTrigger className="h-9 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LCC_SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {formatLccLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className={labelCls}>
            Campaign
            <input value={form.campaign} onChange={set('campaign')} className={inputCls} />
          </label>
          <label className={labelCls}>
            Assigned to
            <Select value={form.assignedTo} onValueChange={setValue('assignedTo')}>
              <SelectTrigger className="h-9 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned">Unassigned</SelectItem>
                {LCC_ASSIGNEES.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className={labelCls}>
            Organization
            <Select value={form.organizationId} onValueChange={setValue('organizationId')}>
              <SelectTrigger className="h-9 text-[13px]">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {(orgsQuery.data?.items ?? []).map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className={labelCls}>
            Contact record
            <Select value={form.contactId} onValueChange={setValue('contactId')}>
              <SelectTrigger className="h-9 text-[13px]">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {(contactsQuery.data?.items ?? []).map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {contact.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className={`${labelCls} sm:col-span-2`}>
            Message / notes
            <textarea
              value={form.message}
              onChange={set('message')}
              rows={3}
              className="flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] font-normal text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </label>
        </div>
        {error != null && (
          <p className="text-[12px] text-destructive">
            {error instanceof Error ? error.message : 'Save failed'}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            size="sm"
            disabled={!form.title.trim() || pending}
            onClick={() => onSubmit(formToPayload(form))}
          >
            {pending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditLeadDialog({
  lead,
  open,
  onOpenChange,
}: {
  lead: LccLead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useLccInvalidate();
  const initial = useMemo<LeadFormState>(
    () => ({
      title: lead.title,
      contactName: lead.contactName ?? '',
      email: lead.email ?? '',
      phone: lead.phone ?? '',
      website: lead.website ?? '',
      campaign: lead.campaign ?? '',
      message: lead.message ?? '',
      source: lead.source,
      assignedTo: lead.assignedTo ?? '__unassigned',
      organizationId: lead.organizationId ?? '__none',
      contactId: lead.contactId ?? '__none',
    }),
    [lead],
  );

  const mutation = useMutation({
    mutationFn: (payload: LccLeadPatch) => lccApi.updateLead(lead.id, payload),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });

  return (
    <LeadFormDialogBase
      open={open}
      onOpenChange={onOpenChange}
      dialogTitle="Edit lead"
      submitLabel="Save changes"
      initial={initial}
      pending={mutation.isPending}
      error={mutation.isError ? mutation.error : null}
      onSubmit={(payload) => mutation.mutate(payload)}
    />
  );
}

export function CreateLeadDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useLccInvalidate();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (payload: LccLeadPatch & { title: string }) => lccApi.createLead(payload),
    onSuccess: (created) => {
      invalidate();
      onOpenChange(false);
      navigate(`/demo/lcc/leads/${created.id}`);
    },
  });

  return (
    <LeadFormDialogBase
      open={open}
      onOpenChange={onOpenChange}
      dialogTitle="New manual lead"
      submitLabel="Create lead"
      initial={EMPTY_FORM}
      pending={mutation.isPending}
      error={mutation.isError ? mutation.error : null}
      onSubmit={(payload) => mutation.mutate(payload)}
    />
  );
}
